import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import Pagination from "../Pagination/Pagination";
import FilterBar from "../FilterBar/FilterBar";
import Loader from "../Loader/Loader";
import { Edit, Trash2, Music, Pause, Play, PlayCircle, PlayIcon } from "lucide-react";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import VoiceCharacter from "./VoiceCharacters";
import ApiService from "../../services/Api.service";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

export default function VoiceCharacterList() {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [headers, setHeaders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });

  const validationSchema = yup.object().shape({});
  const { handleSubmit, control } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      setLoading(true);
      const res = await ApiService.getVoiceCharacters({ page, size });

      if (res.status === 200 && res.data) {
        setTableData(res.data.data || []);
        setPagination(res.data.pagination || {});
        setHeaders(res.data.header || []);
      } else {
        toast.error("Failed to fetch voice characters");
      }
    } catch (error) {
      console.error("Error fetching voice characters:", error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };




  useEffect(() => {
    fetchDetail();
  }, []);

  const handleSubmitFilter = async () => {
    await fetchDetail(1, postsPerPage);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSave = async (data) => {
    try {
      let res;
      if (isEditing && selectedItem) {
        res = await ApiService.updateVoiceCharacter(selectedItem.id, data);
        if (res.status === 200) toast.success("Voice character updated successfully");
        else toast.error("Failed to update voice character");
      } else {
        res = await ApiService.createVoiceCharacter(data);
        if (res.status === 200 || res.status === 201)
          toast.success("Voice character created successfully");
        else toast.error("Failed to create voice character");
      }

      setShowModal(false);
      fetchDetail(pagination.page, postsPerPage);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save voice character");
    }
  };


  const handleDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await ApiService.deleteVoiceCharacter(deleteTarget.id);

      if (res.status === 200 || res.status === 204) {
        toast.success("Voice character deleted successfully");
        fetchDetail(pagination.page, postsPerPage);
      } else {
        toast.error("Failed to delete voice character");
      }
    } catch (error) {
      console.error("Error deleting voice character:", error);
      toast.error("Error deleting voice character");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const playAudio = async (item) => {
    if (playingId === item.id && currentAudio) {
      currentAudio.pause();
      setPlayingId(null);
      setCurrentAudio(null);
      return;
    }

    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }

    try {
      const res = await ApiService.playVoiceCharacter(item.id);
      const audio = new Audio(res.data.signed_url);
      setCurrentAudio(audio);
      setPlayingId(item.id);

      audio.play().catch(() => toast.error("Failed to play audio"));
      audio.onended = () => {
        setPlayingId(null);
        setCurrentAudio(null);
      };
      audio.onerror = () => {
        toast.error("Error playing audio");
        setPlayingId(null);
        setCurrentAudio(null);
      };
    } catch (err) {
      toast.error("Failed to get audio URL");
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      ready: { background: "#dcfce7", color: "#166534" },
      failed: { background: "#fef2f2", color: "#dc2626" },
      pending: { background: "#fef9c3", color: "#a16207" }
    };
    return styles[status] || { background: "#f3f4f6", color: "#6b7280" };
  };


  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(pagination.page, newSize);
  };

  const mistableTh = {
    backgroundColor: "transparent",
    color: "rgba(43, 58, 103, 1)",
    fontFamily: "Nunito",
    fontSize: "13px",
    fontWeight: 700,
    textAlign: "center",
  };

  const tableTdStyles = {
    backgroundColor: "#ffffff",
    fontFamily: "Nunito",
    fontSize: "12px",
    fontWeight: "700",
    textAlign: "center",
    color: "#232122",

  };

  const getTdStyles = (headerValue) => {
    if (["speech_text", "description", "voice_id", "name"].includes(headerValue)) {
      return {
        ...tableTdStyles,
        maxWidth: "200px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }
    return tableTdStyles;
  };

  return (
    <>
      {/* Skeleton loader used inline inside the table container below instead of blocky full-screen spinner */}

      <div className="main-contanier">
        <FilterBar
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={handleSubmitFilter}
          onClearFilters={() => fetchDetail(1, postsPerPage)}
          filters={[]}
          title="Voice Characters"
          totalCount={pagination.total || 0}
          tableId="voice-characters-table"
          isDownload={false}
          isAdd={true}
          onAdd={handleAdd}
        />


        <div className="table-container">
          {loading ? (
            <SkeletonLoader type="table" rows={8} cols={5} />
          ) : (
            <table id="voice-characters-table" className="custom-table-container">
              <thead
                className="table-head"
                style={{ position: "sticky", top: "0", zIndex: "1" }}
              >
                <tr>
                  <th style={mistableTh}>S.No</th>
                  {headers.map((header, i) => (
                    <th key={i} style={mistableTh}>
                      {header.label}
                    </th>
                  ))}
                  <th style={mistableTh}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index}>
                      <td style={tableTdStyles}>{index + 1}</td>
                      {headers.map((header, i) => {
                        const isStatusOrActive = ["is_active", "audio_status"].includes(header.value);
                        const titleText = !isStatusOrActive && item[header.value] ? String(item[header.value]) : undefined;
                        return (
                          <td key={i} style={getTdStyles(header.value)} title={titleText}>
                            {header.value === "is_active"
                              ? item[header.value]
                                ? "Yes"
                                : "No"
                              : header.value === "audio_status"
                                ? (
                                  <span style={{
                                    padding: "4px 10px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    ...getStatusStyle(item[header.value])
                                  }}>
                                    {item[header.value] || "Unknown"}
                                  </span>
                                )
                                : (item[header.value] ?? "--")}
                          </td>
                        );
                      })}
                     <td style={{ ...tableTdStyles, display: 'flex' }}>
                        <button
                          className="icon-btn"
                          onClick={() => playAudio(item)}
                          disabled={item.audio_status !== "ready"}
                          title={playingId === item.id ? "Pause" : (item.audio_status === "ready" ? "Play" : "Audio not available")}
                          style={{
                            marginLeft: "0",
                            opacity: item.audio_status === "ready" ? 1 : 0.5,
                            cursor: item.audio_status === "ready" ? "pointer" : "not-allowed"
                          }}
                        >
                          {playingId === item.id ? (
                            <Pause size={18} color="#3b82f6" />
                          ) : (
                            <PlayIcon size={18} color="#22c55e" />
                          )}
                        </button>

                        <button
                          className="icon-btn edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit Voice character"
                          style={{ marginLeft: "8px" }}
                        >
                          <Edit size={18} color="#f4a261" />
                        </button>

                        <button
                          className="icon-btn delete-btn"
                          onClick={() => handleDelete(item)}
                          title="Delete Voice Character"
                          style={{ marginLeft: "8px" }}
                        >
                          <Trash2 size={18} color="#e63946" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={headers.length + 2} style={tableTdStyles}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <VoiceCharacter
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
            itemData={isEditing ? selectedItem : null}
          />
        )}

        {showDeleteModal && (
          <ConfirmDeleteModal
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}
            onConfirm={handleConfirmDelete}
            itemName={deleteTarget?.label || "this item"}
          />
        )}

        <Pagination
          pagination={pagination}
          postsPerPage={postsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          fetchDetail={fetchDetail}
        />
      </div>
    </>
  );
}

